import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Play, Pause, Plus } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useDepartments } from "@/hooks/useDepartments";
import { getDepartmentIcon } from "@/lib/departmentIcons";
import { departmentUrl } from "@/lib/slugs";
import { Reveal } from "./Reveal";
import type { DepartmentsSettings as DepartmentsSettingsData, StatItem } from "@/hooks/useHomeSettings";

const defaultStats: StatItem[] = [
  { value: "12+", label: "Specialities" },
  { value: "80+", label: "Specialists" },
  { value: "24/7", label: "Emergency" },
];

const AUTOPLAY_MS = 3000;

// Fisher-Yates shuffle
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function useItemsPerPage() {
  const [itemsPerPage, setItemsPerPage] = useState(3);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setItemsPerPage(1);
      else if (w < 1024) setItemsPerPage(2);
      else setItemsPerPage(3);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  return itemsPerPage;
}

export function Departments({ settings }: { settings?: DepartmentsSettingsData | null }) {
  const { departments, isLoading } = useDepartments();

  const heading = settings?.heading || "Centres of Excellence";
  const subtitle = settings?.subtitle || "12+ specialities <span class=\"italic text-foreground/80\">under one roof.</span>";
  const description = settings?.description || "";
  const stats = settings?.stats && settings.stats.length > 0 ? settings.stats : defaultStats;

  const depts = useMemo(() => {
    const mapped = departments.map((d) => ({
      icon: d.icon,
      name: d.name,
      department: d.department,
      desc:
        (d.description ?? "").length > 70
          ? (d.description ?? "").slice(0, 70).trimEnd() + "\u2026"
          : (d.description ?? ""),
    }));
    return shuffle(mapped);
  }, [departments]);

  const itemsPerPage = useItemsPerPage();

  // Chunk departments into pages of `itemsPerPage`
  const pages = useMemo(() => {
    const chunks: typeof depts[] = [];
    for (let i = 0; i < depts.length; i += itemsPerPage) {
      chunks.push(depts.slice(i, i + itemsPerPage));
    }
    return chunks.length > 0 ? chunks : [[]];
  }, [depts, itemsPerPage]);

  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef<number | null>(null);

  // Clamp index when page count changes (e.g. on resize)
  useEffect(() => {
    if (index > pages.length - 1) setIndex(0);
  }, [pages.length, index]);

  const goTo = useCallback(
    (i: number) => setIndex(((i % pages.length) + pages.length) % pages.length),
    [pages.length],
  );
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (!playing || pages.length <= 1) return;
    timerRef.current = window.setTimeout(() => {
      setIndex((i) => (i + 1) % pages.length);
    }, AUTOPLAY_MS);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [index, playing, pages.length]);

  if (isLoading) return null;

  return (
    <section className="relative overflow-hidden bg-surface py-20">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
          backgroundSize: "32px 32px",
          color: "oklch(0.45 0.08 175)",
        }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-background to-transparent" />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mb-12 grid gap-10 lg:grid-cols-2 lg:items-end">
          <div>
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-gold" />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">
                {heading}
              </p>
            </div>
            <h2
              className="font-display text-4xl font-semibold leading-[1.05] text-primary md:text-5xl lg:text-6xl"
              dangerouslySetInnerHTML={{ __html: subtitle }}
            />
            {description && (
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
                {description}
              </p>
            )}
          </div>

          <div className="flex justify-between rounded-2xl border border-border bg-card/70 p-2 backdrop-blur">
            {stats.map((s) => (
              <div key={s.label} className="flex-1 px-4 py-4 text-center border-r border-border last:border-r-0">
                <p className="font-display text-3xl font-semibold text-primary md:text-4xl">
                  {s.value}
                </p>
                <p className="mt-1 text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Slide track — one full page of cards at a time, Hero-style paging */}
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{
              width: `${pages.length * 100}%`,
              transform: `translateX(-${index * (100 / pages.length)}%)`,
            }}
          >
            {pages.map((page, pageIdx) => (
              <div
                key={pageIdx}
                className="grid shrink-0 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                style={{ width: `${100 / pages.length}%` }}
              >
                {page.map(({ icon, name, department, desc }, idx) => {
                  const Icon = getDepartmentIcon(icon);
                  return (
                    <Reveal key={name} delay={idx * 60} className="h-full">
                      <Link
                        to={departmentUrl(name)}
                        className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-7 shadow-soft ring-1 ring-black/[0.02] transition-all duration-500 hover:-translate-y-1 hover:border-gold/50 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                      >
                        <Plus
                          aria-hidden
                          className="absolute right-5 top-5 h-4 w-4 text-gold/60 transition-all duration-500 group-hover:rotate-90 group-hover:text-gold"
                          strokeWidth={1.5}
                        />

                        <span
                          aria-hidden
                          className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary-soft/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                        />

                        <div className="relative flex h-full flex-col">
                          <div className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-soft text-primary transition-all duration-500 group-hover:scale-105">
                            <Icon className="h-7 w-7" strokeWidth={1.5} />
                            <span
                              aria-hidden
                              className="absolute inset-0 rounded-full ring-1 ring-gold/0 transition-all duration-500 group-hover:ring-2 group-hover:ring-gold/60 group-hover:ring-offset-2 group-hover:ring-offset-card"
                            />
                          </div>

                          <h3 className="mt-6 font-display font-space text-xl font-semibold text-foreground transition-colors group-hover:text-primary">
                            {department}
                          </h3>

                          <span
                            aria-hidden
                            className="mt-3 block h-px w-10 bg-gold/50 transition-all duration-500 group-hover:w-20 group-hover:bg-gold"
                          />

                          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                            {desc}
                          </p>

                          <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gold transition-all duration-500">
                            Explore department
                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                          </span>
                        </div>
                      </Link>
                    </Reveal>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Hero-style controls: chevrons + play/pause pill, and progress dots */}
        {pages.length > 1 && (
          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-3">
              {pages.map((_, i) => {
                const active = i === index;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className="group relative h-2 overflow-hidden rounded-full bg-border transition-all"
                    style={{ width: active ? 56 : 22 }}
                  >
                    {active && playing && (
                      <span
                        key={index}
                        className="absolute inset-0 origin-left animate-slide-progress bg-gold"
                        style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
                      />
                    )}
                    {active && !playing && <span className="absolute inset-0 bg-gold" />}
                  </button>
                );
              })}
              <span className="ml-2 font-display text-xs font-medium tracking-widest text-muted-foreground">
                {String(index + 1).padStart(2, "0")}
                <span className="mx-1 text-muted-foreground/50">/</span>
                {String(pages.length).padStart(2, "0")}
              </span>
            </div>

            <div className="flex items-center gap-1 rounded-full border border-border bg-card p-1.5">
              <button
                type="button"
                onClick={prev}
                aria-label="Previous departments"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-primary transition-all hover:bg-primary-soft hover:text-gold"
              >
                <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next departments"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-primary transition-all hover:bg-primary-soft hover:text-gold"
              >
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <span className="mx-1 h-5 w-px bg-border" />
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                aria-label={playing ? "Pause slideshow" : "Play slideshow"}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-primary transition-all hover:bg-primary-soft hover:text-gold"
              >
                {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}